import React from 'react';

import type { AiAction } from '~/components/AiChat/types';
import { findActionExposure } from '~/components/AiChat/utils/actionExposure';
import { useClaim } from '~/components/ClaimContainer';
import { useActionCard } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useActionCard';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';

import ActionConfidence from '../../ActionConfidence';
import ActionHistory from '../../ActionHistory';
import { hasRevisions } from '../../ActionHistory/utils';
import ActionStateChip, { StateChip } from '../../ActionStateChip';
import { ACTION_STATE_APPEARANCE, SUPERSEDED_ACTION_APPEARANCE, SUPERSEDED_ACTION_CHIP } from '../../constants';
import PayloadList from '../../PayloadList';
import type { ActionState } from '../../types';
import { actionTitle, activityCardElementId, activityRowKey } from '../../utils';
import KindCardFrame from '../KindCardFrame';
import type { SupersededAction } from '../types';

import ActionDetail from './ActionDetail';

interface ActionEntryProps {
  notification: StpNotification;
  /** The action row this entry is about, where the claim's actions have been read. */
  action?: AiAction;
  state: ActionState;
  /** Set where a later entry about the action is its card, and this one was about a version since revised. */
  superseded?: SupersededAction;
  /** Refetches the claim's actions after a gesture, which both panes resolve from. */
  onActionUpdate: () => Promise<void>;
  /** Outlines the card: the activity panel last took the reader here. */
  isSelected?: boolean;
  /** Shows a card in the activity panel, by its row's key. */
  onSelectActivityCard?: (key: string) => void;
}

interface ActionEntryFrameProps
  extends Omit<ActionEntryProps, 'onActionUpdate' | 'onSelectActivityCard' | 'superseded'> {
  title?: string | null;
  /** The exposure, where there is one. */
  subtitle?: string;
  /** Shows the action's card in the activity panel. */
  onSelect?: () => void;
  /** The DOM id of the card `onSelect` shows. */
  controls?: string;
  /** Before the state chip: the history icon, where the action was revised. */
  history?: React.ReactNode;
  /** Drawn settled under the Revised chip, in place of the action's state. */
  isSuperseded?: boolean;
  /** Drawn settled under the action's state, as an earlier entry about the version still current. */
  isCurrentVersion?: boolean;
  children?: React.ReactNode;
}

/**
 * The card: what Clive proposed or did, and where it got to.
 *
 * One icon for every action whatever its subject, because the glyph says which *kind* of entry
 * this is and a payment and a reserve movement are both actions. Never a tick: what an action is
 * does not change when it is executed, and the frame and the chip already say what became of it.
 */
const ActionEntryFrame: React.FC<ActionEntryFrameProps> = ({
  notification,
  action,
  state,
  title,
  subtitle,
  history,
  isSuperseded,
  isCurrentVersion,
  isSelected,
  onSelect,
  controls,
  children,
}) => (
  <KindCardFrame
    notification={notification}
    appearance={isSuperseded || isCurrentVersion ? SUPERSEDED_ACTION_APPEARANCE : ACTION_STATE_APPEARANCE[state]}
    kind="recommendation"
    title={title}
    subtitle={subtitle}
    chip={
      <span className="flex min-w-0 items-center gap-4">
        {history}
        {isSuperseded ? <StateChip {...SUPERSEDED_ACTION_CHIP} /> : <ActionStateChip state={state} action={action} />}
      </span>
    }
    confidence={action ? <ActionConfidence action={action} /> : null}
    isSelected={isSelected}
    onSelect={onSelect}
    controls={controls}
  >
    {children}
  </KindCardFrame>
);

/**
 * With its row: titled as the activity panel titles it, with the body and the gestures. The panel
 * lists every row, so the title can show the action's card there.
 */
const JoinedActionEntry: React.FC<ActionEntryProps & { action: AiAction }> = ({
  notification,
  action,
  state,
  onActionUpdate,
  isSelected,
  onSelectActivityCard,
}) => {
  const model = useActionCard(action, onActionUpdate);
  const cardKey = activityRowKey('recommendation', action.action_id);

  return (
    <ActionEntryFrame
      notification={notification}
      action={action}
      state={state}
      title={actionTitle(action)}
      subtitle={model.relatedExposureLabel}
      history={
        hasRevisions(action) ? (
          <ActionHistory action={action} state={state} exposureLabel={model.relatedExposureLabel} />
        ) : undefined
      }
      isSelected={isSelected}
      onSelect={onSelectActivityCard ? () => onSelectActivityCard(cardKey) : undefined}
      controls={activityCardElementId(cardKey)}
    >
      <ActionDetail action={action} state={state} model={model} />
    </ActionEntryFrame>
  );
};

/**
 * Before the action's last entry, about a version that Clive has since revised: titled, valued and scored
 * as that version proposed it, under the Revised chip. When it was decided, the note it was settled with and
 * the gestures are the action's last entry's to show. Until the history holds the version, it is what the
 * entry said. An entry about the version still current (proposed, then decided by the last entry) is drawn
 * the same, settled, but under the action's state: nothing replaced what it proposed.
 */
const SupersededActionEntry: React.FC<
  Omit<ActionEntryProps, 'onActionUpdate' | 'superseded'> & {
    action: AiAction;
    version?: AiAction;
    isCurrent?: boolean;
  }
> = ({ notification, action, version, isCurrent, state, isSelected, onSelectActivityCard }) => {
  const { claim } = useClaim({ claimRequired: true });
  const cardKey = activityRowKey('recommendation', action.action_id);

  return (
    <ActionEntryFrame
      notification={notification}
      action={version}
      state={state}
      title={version ? actionTitle(version) : notification.title}
      subtitle={version ? findActionExposure(version, claim.exposures)?.label_text : undefined}
      history={
        <ActionHistory
          action={action}
          state={state}
          exposureLabel={findActionExposure(action, claim.exposures)?.label_text}
        />
      }
      isSuperseded={!isCurrent}
      isCurrentVersion={isCurrent}
      isSelected={isSelected}
      onSelect={onSelectActivityCard ? () => onSelectActivityCard(cardKey) : undefined}
      controls={activityCardElementId(cardKey)}
    >
      {version ? <PayloadList action={version} state={state} isSuperseded={!isCurrent} /> : null}
    </ActionEntryFrame>
  );
};

/**
 * An entry about a claim action. With its row, it is titled from the row (not the conductor's
 * `✓ <name>`) and offers Execute and Reject while waiting, unless a later entry is the action's card.
 * Without one it is what the notification said, read-only — `useActionCard` needs the row, so it is
 * never called.
 */
const ActionEntry: React.FC<ActionEntryProps> = ({
  notification,
  action,
  state,
  superseded,
  onActionUpdate,
  isSelected,
  onSelectActivityCard,
}) => {
  if (!action) {
    return (
      <ActionEntryFrame notification={notification} state={state} title={notification.title} isSelected={isSelected} />
    );
  }

  return superseded ? (
    <SupersededActionEntry
      notification={notification}
      action={action}
      version={superseded.version}
      isCurrent={superseded.isCurrent}
      state={state}
      isSelected={isSelected}
      onSelectActivityCard={onSelectActivityCard}
    />
  ) : (
    <JoinedActionEntry
      notification={notification}
      action={action}
      state={state}
      onActionUpdate={onActionUpdate}
      isSelected={isSelected}
      onSelectActivityCard={onSelectActivityCard}
    />
  );
};

export default ActionEntry;
